/**
 * Persist deep link data in localStorage.
 * Set on app load when URL has path/query; updated only when app loads again with a different non-empty value.
 */

const STORAGE_KEY = 'deeplinkData';

export interface DeepLinkData {
  path: string;
  search: string;
  queryParameters: Record<string, string>;
}

function isNonEmpty(data: DeepLinkData): boolean {
  const hasPath = data.path !== '' && data.path !== '/';
  const hasQuery = Object.keys(data.queryParameters).length > 0;
  return hasPath || hasQuery;
}

function serialize(data: DeepLinkData): string {
  return JSON.stringify(data);
}

function deserialize(raw: string | null): DeepLinkData | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as DeepLinkData;
    if (
      typeof parsed.path === 'string' &&
      typeof parsed.search === 'string' &&
      parsed.queryParameters &&
      typeof parsed.queryParameters === 'object'
    ) {
      return parsed;
    }
  } catch {
    // ignore
  }
  return null;
}

/**
 * Get persisted deep link data from localStorage.
 */
export function getDeepLinkData(): DeepLinkData | null {
  if (typeof localStorage === 'undefined') return null;
  return deserialize(localStorage.getItem(STORAGE_KEY));
}

/**
 * Persist deep link data. Only writes when the value is non-empty
 * (has a meaningful path or at least one query parameter).
 * Does not overwrite existing stored data with an empty value.
 * Does not overwrite existing query params (e.g. aid) with empty query params.
 */
export function setDeepLinkData(data: DeepLinkData): void {
  if (typeof localStorage === 'undefined') return;
  if (!isNonEmpty(data)) return;
  const existing = getDeepLinkData();
  const hasExistingQuery = existing && Object.keys(existing.queryParameters).length > 0;
  const incomingHasNoQuery = Object.keys(data.queryParameters).length === 0;
  if (hasExistingQuery && incomingHasNoQuery) {
    data = { ...data, queryParameters: existing!.queryParameters, search: existing!.search };
  }
  localStorage.setItem(STORAGE_KEY, serialize(data));
}
